import { useNavigate } from 'react-router';
import { type PanelDetail, type PanelPatient, PIN_PREFIX } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { type Column, DataTable } from '#client/components/ui/DataTable';
import { DetailList } from '#client/components/ui/DetailList';
import { Identifier } from '#client/components/ui/Identifier';
import { Pagination } from '#client/components/ui/Pagination';
import { ResourceDetailPage } from '#client/components/ui/ResourceDetailPage';
import { useResourceList } from '#client/hooks/useResourceList';
import { formatDateTime, dash } from '#client/lib/format';
import { RESOURCE, routes } from '#client/lib/routes';

const PATH = RESOURCE.panels;

const patientColumns: Column<PanelPatient>[] = [
  {
    key: 'pin_no',
    header: 'PIN',
    // Every other screen renders a PIN through Identifier, which strips the
    // `pin_` prefix and sets it in the mono face. This one printed it raw.
    render: (row) => <Identifier value={row.pin_no} stripPrefix={PIN_PREFIX} />,
  },
  { key: 'name', header: 'Name', render: (row) => row.name },
  { key: 'phone_number', header: 'Phone', render: (row) => row.phone_number },
];

/**
 * The panel's patients, a page at a time. A panel collects every visit ever
 * registered under it, so they are paged from their own endpoint rather than
 * arriving inline with the panel.
 */
function PanelPatients({ panel }: { panel: PanelDetail }) {
  const navigate = useNavigate();
  const list = useResourceList<PanelPatient>({ path: `${PATH}/${panel.id}/patients` });

  return (
    <Card title={`Patients (${panel.patient_count})`} flush>
      <DataTable
        columns={patientColumns}
        rows={list.items}
        loading={list.loading}
        rowKey={(row) => row.id}
        caption={`Patients registered under ${panel.name}`}
        emptyState="No patients registered under this panel"
        onRowClick={(row) => void navigate(routes.patient(row.id))}
      />
      <Pagination
        page={list.page}
        limit={list.limit}
        count={list.count}
        onPageChange={list.setPage}
        label="patients"
      />
    </Card>
  );
}

export function PanelDetailPage() {
  return (
    <ResourceDetailPage<PanelDetail>
      path={PATH}
      noun="panel"
      editCapability="panel.edit"
      deleteCapability="panel.delete"
      title={(panel) => panel.name}
      subtitle={(panel) => `Panel ${panel.code}`}
      deleteLabel={(panel) => panel.name}
    >
      {(panel) => (
        <>
          <Card title="Details">
            <DetailList
              items={[
                { label: 'Name', value: panel.name },
                { label: 'Code', value: panel.code },
                { label: 'Owner', value: panel.owner_name },
                { label: 'Discount', value: `${panel.discount_percentage}%` },
                { label: 'Phone', value: panel.phone_number },
                { label: 'Email', value: panel.email },
                { label: 'Created', value: formatDateTime(panel.created_at) },
                { label: 'Contract', value: dash(panel.contract), wide: true },
              ]}
            />
          </Card>

          <PanelPatients panel={panel} />
        </>
      )}
    </ResourceDetailPage>
  );
}
